import React, { useState } from "react";

import { useGetIdentity, useNotification, useTranslation } from "@refinedev/core";

import { Box, Card, CardContent, FormControl, InputLabel, OutlinedInput, Stack, Typography } from "@mui/material";

import { CardButton } from "@/components/common";
import { IUser } from "@/types";
import { CHANGE_NAME_MUTATION, ChangeNameData, ChangeUserNameInput } from "@/utils/GraphQLClient";

import { useMutation } from "urql";

import { Breadcrumb } from "./Breadcrumb";

export const ChangeNamePanel: React.FC = () => {
  const { translate } = useTranslation();
  const { data: user, isLoading: isLoadingIdentity, refetch: refetchIdentity } = useGetIdentity<IUser>();
  const { open: openNotification } = useNotification();

  // urql mutation hook
  const [changeNameResult, changeNameMutation] = useMutation<ChangeNameData, { input: ChangeUserNameInput }>(
    CHANGE_NAME_MUTATION
  );
  const isSubmitting = changeNameResult.fetching;

  // Form state - initialize from user data
  const [firstName, setFirstName] = useState(() => user?.firstName ?? "");
  const [lastName, setLastName] = useState(() => user?.lastName ?? "");

  // UI state
  const [error, setError] = useState<string | null>(null);

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value);
    setError(null);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
    setError(null);
  };

  const isFormValid = firstName.trim() !== "" && lastName.trim() !== "";

  const handleSave = async () => {
    // Validation
    if (!isFormValid) {
      setError(translate("pages.profile.changeName.validationError"));
      return;
    }

    if (!user?.id) {
      setError(translate("pages.profile.changeName.error"));
      return;
    }

    setError(null);

    try {
      const result = await changeNameMutation({
        input: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          user_id: String(user.id),
        },
      });

      if (result.error) {
        setError(
          result.error.graphQLErrors?.[0]?.message ??
            result.error.message ??
            translate("pages.profile.changeName.error")
        );
        return;
      }

      if (result.data?.changeName?.success) {
        // Refresh identity to update user data across the app
        await refetchIdentity();

        openNotification?.({
          type: "success",
          message: translate("pages.profile.changeName.success"),
        });
      } else {
        setError(result.data?.changeName?.message ?? translate("pages.profile.changeName.error"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : translate("pages.profile.changeName.error"));
    }
  };

  const isLoading = isLoadingIdentity || isSubmitting;

  return (
    <Box>
      <Breadcrumb
        items={[
          { label: translate("pages.profile.sections.profileAndSecurity"), to: "/profile?section=profile-security" },
          { label: translate("pages.profile.options.changeName") },
        ]}
      />

      <Card elevation={1}>
        <CardContent>
          <Stack spacing={3}>
            {/* First Name field */}
            <FormControl fullWidth variant="outlined">
              <InputLabel shrink required>
                {translate("pages.profile.fields.firstName")}
              </InputLabel>
              <OutlinedInput
                value={firstName}
                onChange={handleFirstNameChange}
                disabled={isLoading}
                inputProps={{ "data-testid": "first-name-input" }}
              />
            </FormControl>

            {/* Last Name field */}
            <FormControl fullWidth variant="outlined">
              <InputLabel shrink required>
                {translate("pages.profile.fields.lastName")}
              </InputLabel>
              <OutlinedInput
                value={lastName}
                onChange={handleLastNameChange}
                disabled={isLoading}
                inputProps={{ "data-testid": "last-name-input" }}
              />
            </FormControl>

            {/* Error message */}
            {error && (
              <Typography color="error.dark" variant="body2" data-testid="error-message">
                {error}
              </Typography>
            )}

            {/* Save button */}
            <CardButton fullWidth onClick={handleSave} disabled={isLoading || !isFormValid} data-testid="save-button">
              {translate("buttons.save")}
            </CardButton>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
